import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { toggleTheme } from '../../store/slices/uiSlice';
import { logout, clearCredentials } from '../../store/slices/authSlice';
import { fetchPlatformSettings } from '../../store/slices/settingsSlice';
import { Sun, Moon, User, LogOut, Settings, Wallet, ChevronDown, Search, LayoutDashboard, ArrowLeft, Star, Store, Briefcase } from 'lucide-react';
import { buildAvatarUrl } from '../../utils/imageUrl';
import ModernSearchModal from '../search/ModernSearchModal';
import { BrandLockup, LanguageSelector } from '../common';
import NotificationBell from '../notifications/NotificationBell';
import { useMediaQuery } from '../../hooks/useResponsive';
import useInstalledAppView from '../../hooks/useInstalledAppView';

// ──────────────────────────────────────────────────────────────────────────────
// Animated Search Bar — cycles through contextual hints
// ──────────────────────────────────────────────────────────────────────────────
const SEARCH_HINTS = [
  'Search @creators...',
  'Search #trending...',
  'Find live streams...',
  'Browse Game-a-Rama...',
  'Explore the feed...',
  'Browse the Token Exchange...',
];

const SearchBar: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const [hintIndex, setHintIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setHintIndex(i => (i + 1) % SEARCH_HINTS.length);
        setVisible(true);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.button
      whileHover={{ scale: 1.015 }}
      whileTap={{ scale: 0.98 }}
      onClick={onOpen}
      className="hidden md:flex items-center gap-2 flex-1 max-w-xs lg:max-w-md mx-2 lg:mx-5 px-3 py-2 rounded-xl bg-gray-100 dark:bg-[#131a45] border border-gray-200 dark:border-[#1e2a5e] hover:border-emerald-400/60 dark:hover:border-emerald-500/50 hover:shadow-[0_0_0_3px_rgba(16,185,129,0.08)] hover:bg-gray-50 dark:hover:bg-[#0d1333] transition-all duration-200 group min-w-0"
      aria-label="Open search"
    >
      {/* Icon */}
      <Search className="w-4 h-4 text-gray-400 group-hover:text-emerald-500 transition-colors duration-200 flex-shrink-0" />

      {/* Animated placeholder */}
      <span className="flex-1 text-left overflow-hidden h-5 relative">
        <span
          className={`absolute inset-0 text-sm font-medium text-gray-400 dark:text-gray-500 whitespace-nowrap transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
            }`}
        >
          {SEARCH_HINTS[hintIndex]}
        </span>
      </span>

      {/* ⌘K badge */}
      <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-white dark:bg-[#0a0e27] border border-gray-200 dark:border-[#2a3a7e] text-[10px] font-bold font-mono text-gray-400 dark:text-gray-500 tracking-tight shadow-sm flex-shrink-0 group-hover:border-emerald-400/40 group-hover:text-emerald-500 transition-colors duration-200">
        ⌘K
      </kbd>
    </motion.button>
  );
};


const Header: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const { theme } = useAppSelector((state) => state.ui);
  const { balance } = useAppSelector((state) => state.wallet);
  const { settings } = useAppSelector((state) => state.settings);
  const { creatorApplied, creatorStatus, managerApplied, managerStatus, checked: appStatusChecked } = useAppSelector(
    (state) => state.applicationStatus
  );
  const checkingApplication = !appStatusChecked;
  const isMobileHeader = useMediaQuery('(max-width: 1023px)');
  const isInstalledAppView = useInstalledAppView();
  const useAppMobileHeader = isAuthenticated && isMobileHeader && isInstalledAppView;
  // Hide a button if the OTHER application is pending or approved (not rejected)
  const showCreatorBtn = !creatorApplied && !(managerApplied && managerStatus !== 'rejected');
  const showManagerBtn = !managerApplied && !(creatorApplied && creatorStatus !== 'rejected');

  const platformName = settings?.platform_name || '';
  const platformTagline = settings?.platform_tagline || '';

  // Check if we're impersonating a creator
  useEffect(() => {
    const originalTokens = localStorage.getItem('managerOriginalTokens');
    setIsImpersonating(!!originalTokens);
  }, [user]);

  useEffect(() => {
    dispatch(fetchPlatformSettings());
  }, [dispatch]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchModalOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
    // Theme will be applied by App.tsx useEffect
  };

  const handleLogout = async () => {
    localStorage.removeItem('managerOriginalTokens');
    setUserMenuOpen(false);

    try {
      await dispatch(logout()).unwrap();
    } catch {
      dispatch(clearCredentials());
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const handleReturnToManager = () => {
    const originalTokensStr = localStorage.getItem('managerOriginalTokens');
    if (originalTokensStr) {
      try {
        const originalTokens = JSON.parse(originalTokensStr);

        // Restore original manager tokens
        if (originalTokens.accessToken) {
          localStorage.setItem('accessToken', originalTokens.accessToken);
        }
        if (originalTokens.refreshToken) {
          localStorage.setItem('refreshToken', originalTokens.refreshToken);
        }
        if (originalTokens.user) {
          localStorage.setItem('user', originalTokens.user);
        }

        // Clear the impersonation storage
        localStorage.removeItem('managerOriginalTokens');

        // Force page reload to refresh auth state
        window.location.href = '/manager/dashboard';
      } catch (e) {
        console.error('Error returning to manager account:', e);
      }
    }
  };

  return (
    <>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-2 px-3 sm:px-4">
          <div className="max-w-[1800px] mx-auto flex items-center justify-between gap-2">
            <span className="text-xs sm:text-sm font-medium truncate">
              👤 Viewing as: <strong>{user?.username}</strong>
            </span>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleReturnToManager}
              className="flex items-center gap-1.5 px-2.5 sm:px-4 py-1 sm:py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap flex-shrink-0"
            >
              <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Return to Manager</span>
              <span className="xs:hidden">Back</span>
            </motion.button>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white dark:bg-[#0a0e27] border-b border-gray-200 dark:border-[#1e2a5e] shadow-sm">
        <div className="max-w-[1800px] mx-auto px-2 sm:px-4 lg:px-6">
          <div className="relative flex items-center justify-between h-14 sm:h-16 lg:h-[4.5rem] overflow-visible">
            {/* Logo */}
            {/* Logo — flex-1 fills all space left of right-side icons on mobile (no search bar) */}
            <Link
              to={isAuthenticated ? "/feed" : "/"}
              className={`flex items-center gap-1.5 sm:gap-2 lg:gap-3 group min-w-0 ${useAppMobileHeader
                ? 'absolute left-1/2 top-1/2 z-0 w-[min(12rem,58vw)] -translate-x-1/2 -translate-y-1/2 justify-center'
                : 'flex-1 sm:flex-none sm:flex-shrink-0'
                }`}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
                className={`flex items-center ${useAppMobileHeader ? 'justify-center min-w-0' : ''}`}
              >
                <BrandLockup
                  logoUrl={settings?.logo_url}
                  platformName={platformName}
                  platformTagline={platformTagline}
                  size="sm"
                  className="max-w-full"
                  showTagline={!useAppMobileHeader}
                />
              </motion.div>
            </Link>


            {/* Center - Search (for authenticated users) */}
            {isAuthenticated && (
              <SearchBar onOpen={() => setSearchModalOpen(true)} />
            )}

            {/* Right Side Actions */}
            <div className={`flex items-center gap-1 sm:gap-1.5 lg:gap-2.5 flex-shrink-0 ${useAppMobileHeader ? 'relative z-10 ml-auto' : ''}`}>
              {/* Role upgrade buttons — only for plain users, hidden on xs, compact on sm-md, full text on lg+ */}
              {isAuthenticated && user && user.role === 'user' && !checkingApplication && !useAppMobileHeader && (
                <div className="hidden sm:flex items-center gap-1.5">
                  {showCreatorBtn && (
                    <Link to="/profile" title="Become a Creator — create games, play games, and go live" aria-label="Become a Creator — create games, play games, and go live">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center gap-1.5 px-2 md:px-3 lg:px-4 py-2 rounded-lg lg:rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold shadow-md hover:shadow-lg hover:shadow-emerald-500/30 transition-all text-xs lg:text-sm"
                      >
                        <Star className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="hidden md:inline">Become a Creator</span>
                      </motion.button>
                    </Link>
                  )}
                  {showManagerBtn && (
                    <Link to="/profile">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center gap-1.5 px-2 md:px-3 lg:px-4 py-2 rounded-lg lg:rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold shadow-md hover:shadow-lg hover:shadow-teal-500/30 transition-all text-xs lg:text-sm"
                      >
                        <Briefcase className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="hidden md:inline">Become a Manager</span>
                      </motion.button>
                    </Link>
                  )}
                </div>
              )}

              {/* Mobile Search */}
              {isAuthenticated && !useAppMobileHeader && (
                <motion.button
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.92 }}
                  onClick={() => setSearchModalOpen(true)}
                  className="md:hidden p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-gray-100 dark:bg-[#131a45] border border-transparent hover:border-emerald-400/50 hover:bg-white dark:hover:bg-[#0d1333] transition-all group flex-shrink-0"
                  aria-label="Search"
                >
                  <Search className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-emerald-500 transition-colors" />
                </motion.button>
              )}


              {/* Language Selector */}
              {!useAppMobileHeader && <LanguageSelector />}

              {/* Theme Toggle */}
              {!useAppMobileHeader && <motion.button
                whileHover={{ scale: 1.1, rotate: 180 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleThemeToggle}
                className="p-1.5 sm:p-2 lg:p-2.5 rounded-lg sm:rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-[#131a45] dark:hover:bg-[#1e2a5e] transition-all flex-shrink-0"
                aria-label="Toggle theme"
              >
                {theme === 'light' ? (
                  <Moon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 dark:text-gray-300" />
                ) : (
                  <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                )}
              </motion.button>}

              {isAuthenticated && user ? (
                <>
                  {/* Notifications */}
                  <NotificationBell />

                  {useAppMobileHeader && (
                    <div className="relative flex-shrink-0">
                      <motion.button
                        whileTap={{ scale: 0.94 }}
                        onClick={() => setUserMenuOpen((open) => !open)}
                        className="rounded-full bg-emerald-500/15 p-0.5 ring-1 ring-emerald-400/30"
                        aria-label="Open account menu"
                        aria-expanded={userMenuOpen}
                      >
                        <img
                          src={buildAvatarUrl(user.avatar_url) || '/default-avatar.png'}
                          alt={user.username}
                          className="h-8 w-8 rounded-full border-2 border-emerald-400 object-cover"
                        />
                      </motion.button>

                      <AnimatePresence>
                        {userMenuOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -8, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -8, scale: 0.96 }}
                            className="absolute right-0 mt-2 w-[min(13rem,calc(100vw-1rem))] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-[#1e2a5e] dark:bg-[#0d1333]"
                          >
                            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-3">
                              <p className="truncate text-sm font-bold text-white">{user.username}</p>
                              <p className="truncate text-xs text-white/80">{user.email}</p>
                            </div>
                            <div className="p-2">
                              <Link
                                to="/profile"
                                onClick={() => setUserMenuOpen(false)}
                                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-900 transition-all hover:bg-gray-100 dark:text-white dark:hover:bg-[#1e2a5e]"
                              >
                                <User className="h-4 w-4 text-emerald-400" />
                                Profile
                              </Link>
                              <Link
                                to="/dashboard"
                                onClick={() => setUserMenuOpen(false)}
                                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-900 transition-all hover:bg-gray-100 dark:text-white dark:hover:bg-[#1e2a5e]"
                              >
                                <Settings className="h-4 w-4 text-teal-400" />
                                Dashboard
                              </Link>
                              {user.role === 'manager' && (
                                <Link
                                  to="/manager/dashboard"
                                  onClick={() => setUserMenuOpen(false)}
                                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-900 transition-all hover:bg-gray-100 dark:text-white dark:hover:bg-[#1e2a5e]"
                                >
                                  <LayoutDashboard className="h-4 w-4 text-emerald-400" />
                                  Manager
                                </Link>
                              )}
                              <Link
                                to="/wallet"
                                onClick={() => setUserMenuOpen(false)}
                                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-900 transition-all hover:bg-gray-100 dark:text-white dark:hover:bg-[#1e2a5e]"
                              >
                                <Wallet className="h-4 w-4 text-cyan-400" />
                                Wallet
                              </Link>
                              <button
                                onClick={handleLogout}
                                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-red-500 transition-all hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <LogOut className="h-4 w-4" />
                                Logout
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}

                  {/* Wallet Balance - Hidden on mobile, shown in menu */}
                  {!useAppMobileHeader && <Link to="/wallet?action=purchase" className="hidden sm:flex flex-shrink-0">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center gap-1.5 px-2.5 sm:px-3 lg:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg sm:rounded-xl text-white font-bold shadow-md text-xs sm:text-sm cursor-pointer hover:shadow-lg hover:shadow-emerald-500/30 transition-all"
                      title="Click to recharge"
                    >
                      <Wallet className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="max-w-[5rem] truncate">{balance.toLocaleString()}</span>
                    </motion.div>
                  </Link>}

                  {/* User Menu */}
                  {!useAppMobileHeader && <div className="relative flex-shrink-0">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="flex items-center gap-1 sm:gap-1.5 px-1 sm:px-1.5 lg:px-2 py-1 sm:py-1.5 rounded-lg sm:rounded-xl bg-gray-100 hover:bg-gray-200 dark:hover:bg-gray-100 dark:bg-[#131a45] transition-all"
                    >
                      <img
                        src={buildAvatarUrl(user.avatar_url) || '/default-avatar.png'}
                        alt={user.username}
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 border-emerald-500 object-cover flex-shrink-0"
                      />
                      <ChevronDown className={`hidden sm:block w-3.5 h-3.5 text-gray-600 dark:text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                    </motion.button>

                    <AnimatePresence>
                      {userMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                          className="absolute right-0 mt-2 w-[min(13rem,calc(100vw-1rem))] sm:w-56 bg-white dark:bg-[#0d1333] rounded-xl sm:rounded-2xl shadow-2xl border border-gray-200 dark:border-[#1e2a5e] overflow-hidden z-50"
                        >
                          <div className="p-3 sm:p-4 bg-gradient-to-r from-emerald-500 to-teal-500">
                            <p className="text-white font-bold truncate text-sm sm:text-base">{user.username}</p>
                            <p className="text-white/80 text-xs sm:text-sm truncate">{user.email}</p>
                          </div>
                          <div className="p-1.5 sm:p-2">
                            {/* Role upgrade buttons in dropdown — for plain users */}
                            {user.role === 'user' && !checkingApplication && (
                              <div className="flex gap-2 mb-2">
                                {showCreatorBtn && (
                                  <Link
                                    to="/profile"
                                    onClick={() => setUserMenuOpen(false)}
                                    className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 transition-all"
                                  >
                                    <Star className="w-4 h-4 flex-shrink-0" />
                                    <span className="font-bold text-[11px] sm:text-xs leading-tight">Creator</span>
                                  </Link>
                                )}
                                {showManagerBtn && (
                                  <Link
                                    to="/profile"
                                    onClick={() => setUserMenuOpen(false)}
                                    className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600 transition-all"
                                  >
                                    <Briefcase className="w-4 h-4 flex-shrink-0" />
                                    <span className="font-bold text-[11px] sm:text-xs leading-tight">Manager</span>
                                  </Link>
                                )}
                              </div>
                            )}
                            <Link
                              to="/profile"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-100 dark:bg-[#131a45] dark:hover:bg-[#1e2a5e] transition-all"
                            >
                              <User className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                              <span className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm">Profile</span>
                            </Link>
                            <Link
                              to="/dashboard"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-100 dark:bg-[#131a45] dark:hover:bg-[#1e2a5e] transition-all"
                            >
                              <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-teal-400" />
                              <span className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm">Dashboard</span>
                            </Link>
                            {user.role === 'manager' && (
                              <Link
                                to="/manager/dashboard"
                                onClick={() => setUserMenuOpen(false)}
                                className="flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-100 dark:bg-[#131a45] dark:hover:bg-[#1e2a5e] transition-all"
                              >
                                <LayoutDashboard className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                                <span className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm">Manager Dashboard</span>
                              </Link>
                            )}
                            <Link
                              to="/otc/seller/dashboard"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-100 dark:bg-[#131a45] dark:hover:bg-[#1e2a5e] transition-all"
                            >
                              <Store className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-400" />
                              <span className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm">Token Exchange</span>
                            </Link>
                            <Link
                              to="/wallet"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-100 dark:bg-[#131a45] dark:hover:bg-[#1e2a5e] transition-all sm:hidden"
                            >
                              <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
                              <span className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm">Wallet ({balance})</span>
                            </Link>
                            <button
                              onClick={handleLogout}
                              className="w-full flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-all text-red-400"
                            >
                              <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
                              <span className="font-medium text-xs sm:text-sm">Logout</span>
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>}
                </>
              ) : (
                <div className="flex items-center gap-1 sm:gap-2">
                  <Link to="/login">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-2.5 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-50 dark:bg-[#0d1333] transition-all whitespace-nowrap"
                    >
                      Login
                    </motion.button>
                  </Link>
                  <Link to="/register">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-2.5 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md hover:shadow-lg hover:shadow-emerald-500/30 transition-all whitespace-nowrap"
                    >
                      Sign Up
                    </motion.button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search Modal */}
        <ModernSearchModal
          isOpen={searchModalOpen}
          onClose={() => setSearchModalOpen(false)}
        />
      </header>
    </>
  );
};

export default Header;
